import React, { useState } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedButton } from "@/components/ThemedButton";
import { DateDisplay } from "@/components/date/DateDisplay";
import { DateSelector } from "@/components/date/DateSelector";
import { useThemeColor } from "@/hooks/useThemeColor";
import { IDate } from "common247";

/**
 * Interactive Design Workbench for MemoryDate component.
 * Allows sign-off review of exact dates, approximate dates, timestamps,
 * year/month, year-only, and multi-day / multi-month date ranges before integration.
 */
export default function MemoryDateDesignScreen() {
  const tintColor = useThemeColor({}, "tint");
  const dangerColor = useThemeColor({}, "danger");
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const readOnlyBg = useThemeColor({}, "inputReadOnly");

  // Interactive sandbox state
  const [interactivePayload, setInteractivePayload] = useState<IDate | string>(
    '{"mode":"range","from":{"precision":"exact_date","year":2026,"month":10,"day":9,"dateStr":"09-Oct-2026"},"to":{"precision":"exact_date","year":2026,"month":10,"day":10,"dateStr":"10-Oct-2026"},"formatted":"09-Oct-2026 to 10-Oct-2026"}'
  );
  const [isValidState, setIsValidState] = useState<boolean>(true);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [pickerModalOpen, setPickerModalOpen] = useState<boolean>(false);
  const [pickerAnchor, setPickerAnchor] = useState<{ x: number; y: number } | null>(null);

  // Presets demonstrated from prompt specification
  const promptExamples = [
    {
      title: "1. Exact Date / Time (dd-MMM-yyyy HH:mm)",
      description: "Specific calendar moment with time to the minute (no seconds).",
      sample: "09-Oct-2026 16:20",
      useCase: "Camera EXIF capture, event timestamp",
    },
    {
      title: "2. Exact Date Only (dd-MMM-yyyy)",
      description: "Calendar day in standard dd-MMM-yyyy format.",
      sample: "09-Oct-2026",
      useCase: "Birthday, specific anniversary day, scanned letter date",
    },
    {
      title: "3. Approximate Date / Time (~)",
      description: "Estimated moment within a particular hour with ~ symbol (no seconds).",
      sample: "~ 09-Oct-2026 13:00",
      useCase: "Recall memory: 'Around 1pm on 09-Oct-2026'",
    },
    {
      title: "4. Approximate Date (~)",
      description: "Estimated calendar day with ~ symbol.",
      sample: "~ 09-Oct-2026",
      useCase: "Old family photo or event remembered roughly by day",
    },
    {
      title: "5. Year / Month (MMM Format)",
      description: "Month-level precision in MMM format (e.g. Oct 2026).",
      sample: "Oct 2026",
      useCase: "Vacation month, seasonal memory (e.g. 'Oct 2026')",
    },
    {
      title: "6. Year Only",
      description: "Broad historical or era milestone.",
      sample: "2026",
      useCase: "Vintage photo, childhood memory, historical event",
    },
    {
      title: "7. Date Range: Wedding Day to Following Day",
      description: "Multi-day span for photo collections and events.",
      sample: "09-Oct-2026 to 10-Oct-2026",
      useCase: "Wedding photos, weekend getaway, multi-day conference",
    },
    {
      title: "8. Mixed Precision Range: Multi-Month Vacation (MMM)",
      description: "Any combination of from and to precisions in MMM format.",
      sample: "Oct 2026 to Nov 2026",
      useCase: "Extended holiday trip, semester abroad, seasonal span",
    },
  ];

  return (
    <ThemedScreen
      title="Design: Memory Date Component"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        {/* Sign-off overview banner */}
        <ThemedPanel padding={14} elevation="low">
          <View style={styles.bannerHeader}>
            <View style={[styles.iconBox, { backgroundColor: badgeBg }]}>
              <Ionicons name="calendar-sharp" size={22} color={tintColor} />
            </View>
            <View style={{ flex: 1 }}>
              <ThemedText type="defaultSemiBold" style={styles.bannerTitle}>
                Memory Date Representation Component
              </ThemedText>
              <ThemedText style={[styles.bannerSub, { color: textMuted }]}>
                Design Sign-off Workbench (Feature 194)
              </ThemedText>
            </View>
          </View>
          <ThemedText style={styles.bannerText}>
            This control supports exact dates, timestamps, approximate dates, month/year, year-only,
            and arbitrary date ranges. It serializes to clean, unpackable JSON and is backward-compatible
            with existing memory date strings.
          </ThemedText>
        </ThemedPanel>

        {/* SECTION 1: SPECIFICATION EXAMPLES */}
        <View style={styles.sectionHeaderRow}>
          <ThemedText type="stamp" style={{ color: textMuted }}>
            PROMPT TEST EXAMPLES (DISPLAY VARIANTS)
          </ThemedText>
        </View>

        <View style={styles.examplesGrid}>
          {promptExamples.map((item, idx) => (
            <ThemedPanel key={idx} padding={12} elevation="low" style={styles.exampleCard}>
              <View style={styles.exampleHeader}>
                <ThemedText type="defaultSemiBold" style={styles.exampleTitle}>
                  {item.title}
                </ThemedText>
                <TouchableOpacity
                  style={[styles.loadBtn, { borderColor: tintColor }]}
                  onPress={() => {
                    setInteractivePayload(item.sample);
                  }}
                  activeOpacity={0.7}
                >
                  <ThemedText style={[styles.loadBtnText, { color: tintColor }]}>
                    Load into Sandbox
                  </ThemedText>
                </TouchableOpacity>
              </View>

              <ThemedText style={[styles.exampleDesc, { color: textMuted }]}>
                {item.description}
              </ThemedText>

              {/* Rendered variants */}
              <View style={styles.variantsRow}>
                <View style={styles.variantBlock}>
                  <ThemedText style={[styles.variantLabel, { color: textMuted }]}>Badge:</ThemedText>
                  <DateDisplay value={item.sample} variant="badge" />
                </View>

                <View style={styles.variantBlock}>
                  <ThemedText style={[styles.variantLabel, { color: textMuted }]}>Pill:</ThemedText>
                  <DateDisplay value={item.sample} variant="pill" />
                </View>
              </View>

              <View style={[styles.useCaseBox, { backgroundColor: badgeBg }]}>
                <ThemedText style={[styles.useCaseText, { color: textMuted }]}>
                  💡 <ThemedText type="defaultSemiBold" style={{ fontSize: 11 }}>Use Case: </ThemedText>
                  {item.useCase}
                </ThemedText>
              </View>
            </ThemedPanel>
          ))}
        </View>

        {/* SECTION 2: INTERACTIVE SANDBOX */}
        <View style={styles.sectionHeaderRow}>
          <ThemedText type="stamp" style={{ color: textMuted }}>
            INTERACTIVE LIVE SANDBOX & JSON INSPECTOR
          </ThemedText>
        </View>

        <ThemedPanel padding={14} elevation="medium">
          <ThemedText type="subtitle" style={{ marginBottom: 4 }}>
            Interactive MemoryDate Editor
          </ThemedText>
          <ThemedText style={[styles.bannerSub, { color: textMuted, marginBottom: 12 }]}>
            Toggle between Single Date and Date Range, change precisions, and watch the JSON payload update live.
          </ThemedText>

          <DateDisplay
            value={interactivePayload}
            mode="editor"
            showJsonInspector={true}
            onChange={(payload) => {
              setInteractivePayload(payload);
            }}
            onValidationChange={(isValid, errors) => {
              setIsValidState(isValid);
              setValidationErrors(errors);
            }}
          />

          {/* Validation Test Bench */}
          <View style={{ marginTop: 12, padding: 10, borderRadius: 8, backgroundColor: readOnlyBg || cardBg, borderWidth: 1, borderColor }}>
            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <ThemedText type="defaultSemiBold" style={{ fontSize: 12 }}>
                Validation Status:
              </ThemedText>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                <Ionicons
                  name={isValidState ? "checkmark-circle" : "alert-circle"}
                  size={14}
                  color={isValidState ? "#10B981" : dangerColor}
                />
                <ThemedText style={{ fontSize: 12, fontWeight: "700", color: isValidState ? "#10B981" : dangerColor }}>
                  {isValidState ? "Valid Date Payload" : "Invalid Input Detected"}
                </ThemedText>
              </View>
            </View>

            {validationErrors.length > 0 && (
              <View style={{ gap: 2, marginBottom: 8 }}>
                {validationErrors.map((err, i) => (
                  <ThemedText key={i} style={{ fontSize: 11, color: dangerColor, fontWeight: "500" }}>
                    • {err}
                  </ThemedText>
                ))}
              </View>
            )}

            <ThemedText style={{ fontSize: 11, color: textMuted, marginBottom: 6 }}>
              Test Validation Edge Cases:
            </ThemedText>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
              <TouchableOpacity
                style={[styles.loadBtn, { borderColor: dangerColor }]}
                onPress={() => {
                  setInteractivePayload(
                    '{"mode":"range","from":{"precision":"exact_date","year":2026,"month":10,"day":15,"dateStr":"15-Oct-2026"},"to":{"precision":"exact_date","year":2026,"month":10,"day":9,"dateStr":"09-Oct-2026"},"formatted":"15-Oct-2026 to 09-Oct-2026"}'
                  );
                }}
              >
                <ThemedText style={[styles.loadBtnText, { color: dangerColor }]}>
                  Test Inverted Range
                </ThemedText>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.loadBtn, { borderColor: tintColor }]}
                onPress={() => {
                  setInteractivePayload(
                    '{"mode":"single","single":{"precision":"exact_date","year":2026,"month":2,"day":28,"dateStr":"28-Feb-2026"},"formatted":"28-Feb-2026"}'
                  );
                }}
              >
                <ThemedText style={[styles.loadBtnText, { color: tintColor }]}>
                  Test Feb Clamping
                </ThemedText>
              </TouchableOpacity>
            </View>
          </View>

          {/* Real-time Display Mode Previews */}
          <View style={[styles.previewSection, { borderColor }]}>
            <ThemedText type="defaultSemiBold" style={{ fontSize: 13, marginBottom: 8 }}>
              Live Consumer Display Preview (How It Renders on Memory Cards):
            </ThemedText>

            <View style={styles.previewRow}>
              <View style={styles.previewItem}>
                <ThemedText style={[styles.variantLabel, { color: textMuted }]}>Standard Badge:</ThemedText>
                <DateDisplay value={interactivePayload} variant="badge" />
              </View>

              <View style={styles.previewItem}>
                <ThemedText style={[styles.variantLabel, { color: textMuted }]}>Compact Pill:</ThemedText>
                <DateDisplay value={interactivePayload} variant="pill" />
              </View>
            </View>

            <View style={[styles.previewCardBox, { backgroundColor: cardBg, borderColor }]}>
              <View style={styles.cardHeaderPreview}>
                <ThemedText type="defaultSemiBold" style={{ fontSize: 14 }}>
                  Sample Memory Card Header
                </ThemedText>
                <DateDisplay value={interactivePayload} variant="badge" />
              </View>
              <ThemedText style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                "Our Wedding Weekend in Florence, Italy" • 12 photos captured
              </ThemedText>
            </View>
          </View>
        </ThemedPanel>

        {/* SECTION 3: VISUAL MEMORY DATE PICKER & ANALOG CLOCK DIAL */}
        <View style={styles.sectionHeaderRow}>
          <ThemedText type="stamp" style={{ color: textMuted }}>
            VISUAL PICKER & ANALOG CLOCK DIAL (FEATURE 194)
          </ThemedText>
        </View>

        <ThemedPanel padding={14} elevation="low">
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <View style={[styles.iconBox, { backgroundColor: badgeBg }]}>
              <Ionicons name="time-outline" size={22} color={tintColor} />
            </View>
            <View style={{ flex: 1 }}>
              <ThemedText type="defaultSemiBold" style={{ fontSize: 15 }}>
                DatePicker Control with Analog Dial
              </ThemedText>
              <ThemedText style={[styles.bannerSub, { color: textMuted }]}>
                Calendar grid + Circular 12-hour analog clock dial
              </ThemedText>
            </View>
          </View>

          <ThemedText style={[styles.bannerText, { marginBottom: 12 }]}>
            Users can either type the date and time manually or open the visual DatePicker.
            The picker includes an interactive analog clock dial (hour hand, minute hand, radial numbers,
            and AM/PM toggle) as well as full calendar selection for single dates and date ranges.
          </ThemedText>

          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <ThemedButton
              title="Open DatePicker Dialog"
              variant="primary"
              onPress={(e?: any) => {
                if (e?.nativeEvent?.pageX !== undefined && e?.nativeEvent?.pageY !== undefined) {
                  setPickerAnchor({ x: e.nativeEvent.pageX, y: e.nativeEvent.pageY });
                }
                setPickerModalOpen(true);
              }}
            />
            <ThemedButton
              title="Reset to Wedding Range"
              variant="secondary"
              onPress={() => {
                setInteractivePayload(
                  '{"mode":"range","from":{"precision":"exact_date","year":2026,"month":10,"day":9,"dateStr":"09-Oct-2026"},"to":{"precision":"exact_date","year":2026,"month":10,"day":10,"dateStr":"10-Oct-2026"},"formatted":"09-Oct-2026 to 10-Oct-2026"}'
                );
              }}
            />
          </View>

          <View style={[styles.useCaseBox, { backgroundColor: badgeBg }]}>
            <ThemedText style={[styles.useCaseText, { color: textMuted }]}>
              ⏰ <ThemedText type="defaultSemiBold" style={{ fontSize: 11 }}>Analog Dial Features: </ThemedText>
              Interactive clock hand geometry, 12 radial hour positions, 5-minute interval ticks, AM/PM switcher, and quick toggle between Calendar and Clock stages.
            </ThemedText>
          </View>
        </ThemedPanel>

        {/* Dedicated DateSelector Modal for Standalone Workbench Testing */}
        <DateSelector
          visible={pickerModalOpen}
          anchorPosition={pickerAnchor}
          onClose={() => setPickerModalOpen(false)}
          value={interactivePayload}
          type="Range"
          onApply={(pickedPayload) => {
            setInteractivePayload(pickedPayload);
            setPickerModalOpen(false);
          }}
        />
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 14,
    gap: 16,
  },
  bannerHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  iconBox: {
    width: 40,
    height: 40,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  bannerTitle: {
    fontSize: 16,
  },
  bannerSub: {
    fontSize: 12,
  },
  bannerText: {
    fontSize: 13,
    lineHeight: 18,
    marginTop: 4,
  },
  sectionHeaderRow: {
    marginTop: 6,
    marginBottom: -4,
  },
  examplesGrid: {
    gap: 10,
  },
  exampleCard: {
    gap: 8,
  },
  exampleHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  exampleTitle: {
    fontSize: 14,
  },
  loadBtn: {
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  loadBtnText: {
    fontSize: 11,
    fontWeight: "600",
  },
  exampleDesc: {
    fontSize: 12,
    lineHeight: 16,
  },
  variantsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    alignItems: "center",
    marginVertical: 4,
  },
  variantBlock: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  variantLabel: {
    fontSize: 11,
    fontWeight: "600",
  },
  useCaseBox: {
    padding: 8,
    borderRadius: 6,
  },
  useCaseText: {
    fontSize: 11,
    lineHeight: 15,
  },
  previewSection: {
    marginTop: 14,
    paddingTop: 14,
    borderTopWidth: 1,
  },
  previewRow: {
    flexDirection: "row",
    gap: 20,
    alignItems: "center",
    marginBottom: 12,
  },
  previewItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  previewCardBox: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  cardHeaderPreview: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
});
